import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  isLowPowerDevice,
  mediaQueryLarge,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {import('./slideshow').Slideshow} zoomSlideshow - The slideshow component that renders the zoomed media strip.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'zoomSlideshow', 'thumbnails'];

  connectedCallback() {
    super.connectedCallback();
    this.refs.zoomSlideshow.addEventListener(SlideshowSelectEvent.eventName, this.handleSlideshowSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.zoomSlideshow.removeEventListener(SlideshowSelectEvent.eventName, this.handleSlideshowSelect);
  }

  /**
   * The zoomed media elements (one per `<slideshow-slide>` in the zoomed media strip), in display order.
   * @returns {HTMLElement[]}
   */
  get media() {
    return this.refs.zoomSlideshow.refs.slides ?? [];
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, zoomSlideshow } = this.refs;
    const targetImage = this.media[index];

    const open = () => {
      dialog.showModal();

      // `zoomSlideshow` connects/initializes while the dialog is still `display: none` (the
      // native <dialog> UA default before showModal()), so its own initial clone-loop landing
      // correction (see Slideshow#verifyClonelessLanding()'s doc comment) can't reliably
      // measure geometry at that point - it can end up resting on a boundary clone with no
      // scroll event ever firing to self-correct. Re-verify now that showModal() has just made
      // geometry valid, BEFORE select() below: this only fixes up physical scroll position
      // (dispatches nothing), so select()'s own same-index no-op guard (which would otherwise
      // skip correcting this exact case when `index` already matches `current`, e.g. opening
      // the first slide) still runs cleanly on top of a now-correct starting position.
      zoomSlideshow.verifyClonelessLanding();

      // Dispatches SlideshowSelectEvent, which handleSlideshowSelect() picks up to
      // sync the thumbnail rail's aria-selected state and scroll position. select()
      // no-ops when index already matches the slideshow's current slide (e.g.
      // opening the first slide, since the slideshow initializes on slide 0).
      zoomSlideshow.select(index, undefined, { animate: false });
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = this.#resolveFocalPoint(sourceImage);
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');
  }

  /**
   * Handles the zoomed media strip's `slideshow:select` event (dispatched on programmatic
   * select, user drag, and manual scroll alike) to keep the thumbnail rail in sync.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  handleSlideshowSelect = (event) => {
    const { thumbnails } = this.refs;
    const { index } = event.detail;

    const targetThumbnail = thumbnails.children[index];
    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
      block: 'center',
      inline: 'center',
    });

    // Note: this used to also dispatch a ZoomMediaSelectedEvent for media-gallery.js to relay
    // into the main gallery's slideshow. That's now handled directly by media-gallery.js's own
    // symmetric SlideshowSelectEvent listener pair (it listens on this element's zoomSlideshow
    // ref itself), so no separate relay event is needed here.
  };

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, zoomSlideshow } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    const activeIndex = zoomSlideshow.current;
    const mostVisibleElement = this.media[activeIndex];

    if (!mostVisibleElement) return this.closeDialog();

    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = this.#resolveFocalPoint(slide);
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Resolves an element's view-transition focal point, preferring
   * `data-focal-point-desktop` on desktop viewports (`mediaQueryLarge`,
   * min-width: 750px - the same cached `MediaQueryList` singleton already used
   * throughout `assets/` for this breakpoint) - see
   * `product-media-mood-image.liquid`'s `desktop_focal_point` param doc and
   * `snippets/product-media-gallery-content.liquid`'s grid-presentation `<li>`
   * for where that attribute is emitted. Falls back to the existing
   * `data-focal-point` when the desktop-specific one isn't present, or on
   * viewports below the breakpoint - matching the same 750px split used
   * throughout the gallery's desktop/mobile behavior (mobile_only, the CSS
   * --focal-point-desktop rule, etc.).
   * @param {HTMLElement} element
   * @returns {string | undefined}
   */
  #resolveFocalPoint(element) {
    if (mediaQueryLarge.matches && element.dataset.focalPointDesktop) {
      return element.dataset.focalPointDesktop;
    }
    return element.dataset.focalPoint;
  }

  /**
   * Closes the dialog when the user presses the escape key, and navigates the zoomed media strip
   * when the user presses the left/right arrow keys.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    event.preventDefault();

    const { zoomSlideshow } = this.refs;
    const options = { animate: !prefersReducedMotion() };

    if (event.key === 'ArrowLeft') {
      zoomSlideshow.previous(undefined, options);
    } else {
      zoomSlideshow.next(undefined, options);
    }
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    this.refs.zoomSlideshow.select(index, undefined, { animate: options.behavior !== 'instant' });
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}
